import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../lib/api.js";
import { formatIsoDate, toLocalDateString } from "../../lib/date.js";
import { DATE_POPOVER_DOWNWARDS } from "../../theme.js";
import {
  useCompleteMaintenanceEvent,
  useCreateMaintenanceEvent,
  useDiscardMaintenanceEvent,
  useProducts,
  useSystemEvents,
  useVersions,
  type MaintenanceEvent,
} from "../../hooks/useMaintenance.js";
import type { SkippedSoftwareState } from "@msc/shared";
import { SoftwarePicker } from "./SoftwarePicker.js";

const STATUS_COLORS: Record<MaintenanceEvent["status"], string> = {
  geplant: "blue",
  durchgefuehrt: "green",
  verworfen: "gray",
};

interface Props {
  customerId: string;
  systemId: string;
}

/**
 * Abschnitt „Wartungsvorgänge" im aufgeklappten System (Aufgabe 16) — der Kern
 * der Wartungsplanung: ein Vorgang trägt Zielstände, und beim Abschließen
 * schreibt sich der Systemstand daraus fort (E1), statt getrennt gepflegt zu
 * werden. `useCompleteMaintenanceEvent` invalidiert deshalb neben der
 * Vorgangsliste auch die Systemliste des Kunden — sonst zeigt der
 * Softwarestände-Tab nach dem Abschließen weiter den alten Stand.
 */
export function SystemMaintenanceEvents({ customerId, systemId }: Props) {
  const { t } = useTranslation();
  const { data: events, isLoading, isError } = useSystemEvents(systemId);
  const [planning, setPlanning] = useState(false);

  return (
    <Box>
      <Group justify="space-between" mb={4}>
        <Text fw={600} size="sm">
          {t("maintenance.card.events.title")}
        </Text>
        <Button size="compact-xs" variant="light" onClick={() => setPlanning((v) => !v)}>
          {t("maintenance.card.events.planButton")}
        </Button>
      </Group>

      {planning && <NewEventForm systemId={systemId} onDone={() => setPlanning(false)} />}

      {isError ? (
        <Text c="red" size="sm">
          {t("common.errorLoading")}
        </Text>
      ) : isLoading ? (
        // Befund U12: Beim Aufklappen eines Systems stand hier nur ein
        // winziges „Lädt…" — die Fläche wirkte leer und defekt. Der Umriss
        // der kommenden Vorgangstabelle sagt dasselbe sichtbarer.
        <Skeleton height={90} mt="xs" />
      ) : (events ?? []).length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("maintenance.card.events.empty")}
        </Text>
      ) : (
        <Table withTableBorder withColumnBorders mt="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("maintenance.card.events.tableTitle")}</Table.Th>
              <Table.Th>{t("maintenance.card.events.tableStatus")}</Table.Th>
              <Table.Th>{t("maintenance.card.events.tablePlannedFor")}</Table.Th>
              <Table.Th>{t("maintenance.card.events.tableExecutedOn")}</Table.Th>
              <Table.Th>{t("maintenance.card.events.tableTargets")}</Table.Th>
              <Table.Th>{t("common.actions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(events ?? []).map((event) => (
              <EventRow key={event.id} customerId={customerId} systemId={systemId} event={event} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}

function EventRow({
  customerId,
  systemId,
  event,
}: {
  customerId: string;
  systemId: string;
  event: MaintenanceEvent;
}) {
  const { t } = useTranslation();
  // Ein Formular je Zeile: „durchgeführt" und „verworfen" sind sich
  // ausschließende Ausgänge — zwei gleichzeitig offene Eingabefelder würden
  // genau die Verwechslung nahelegen, die diese Zeile verhindern soll.
  const [openForm, setOpenForm] = useState<"complete" | "discard" | null>(null);
  const toggle = (which: "complete" | "discard") =>
    setOpenForm((prev) => (prev === which ? null : which));

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Text size="sm">{event.title}</Text>
          {/* Die Begründung steht an der Zeile und nicht hinter einem Klick:
              Warum eine Wartung ausfiel, ist beim nächsten Planen die
              wichtigste Information über diesen Vorgang. */}
          {event.discardReason && (
            <Text c="dimmed" size="xs">
              {t("maintenance.card.events.discardReasonLabel", { reason: event.discardReason })}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Badge color={STATUS_COLORS[event.status]} variant="light">
            {t(`maintenance.card.events.status.${event.status}`)}
          </Badge>
        </Table.Td>
        <Table.Td>{formatIsoDate(event.plannedFor)}</Table.Td>
        <Table.Td>{formatIsoDate(event.executedOn)}</Table.Td>
        <Table.Td>{event.targets.map((tgt) => `${tgt.productName} ${tgt.versionLabel}`).join(", ")}</Table.Td>
        <Table.Td>
          {event.status === "geplant" && (
            <Group gap={4} wrap="nowrap">
              <Button size="compact-xs" variant="subtle" onClick={() => toggle("complete")}>
                {t("maintenance.card.events.completeButton")}
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={() => toggle("discard")}
              >
                {t("maintenance.card.events.discardButton")}
              </Button>
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
      {openForm === "complete" && (
        <Table.Tr>
          <Table.Td colSpan={6}>
            <CompleteEventForm
              customerId={customerId}
              systemId={systemId}
              eventId={event.id}
              onDone={() => setOpenForm(null)}
            />
          </Table.Td>
        </Table.Tr>
      )}
      {openForm === "discard" && (
        <Table.Tr>
          <Table.Td colSpan={6}>
            <DiscardEventForm
              systemId={systemId}
              eventId={event.id}
              onDone={() => setOpenForm(null)}
            />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

/**
 * Vorgang verwerfen — der Ausgang für eine Wartung, die nicht stattfindet.
 *
 * Der Hinweis über dem Feld ist kein Beiwerk: Ohne diesen Weg war „als
 * durchgeführt markieren" der einzige Weg, einen ausgefallenen Vorgang
 * loszuwerden — und der schreibt den Softwarestand auf eine Version fort, die
 * nie eingespielt wurde. Der Satz sagt, warum dieser Knopf der richtige ist.
 */
function DiscardEventForm({
  systemId,
  eventId,
  onDone,
}: {
  systemId: string;
  eventId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const discardEvent = useDiscardMaintenanceEvent(systemId);
  const [reason, setReason] = useState("");
  const canSubmit = reason.trim().length > 0;

  return (
    <Stack gap="xs">
      <Text c="dimmed" size="xs">
        {t("maintenance.card.events.discardHint")}
      </Text>
      <Textarea
        size="xs"
        autosize
        minRows={2}
        label={t("maintenance.card.events.discardReason")}
        placeholder={t("maintenance.card.events.discardReasonPlaceholder")}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        style={{ maxWidth: 480 }}
      />
      <Group gap="xs">
        <Button
          size="xs"
          color="red"
          disabled={!canSubmit}
          loading={discardEvent.isPending}
          onClick={() => {
            if (!canSubmit) return;
            discardEvent.mutate({ eventId, reason: reason.trim() }, { onSuccess: onDone });
          }}
        >
          {t("maintenance.card.events.discardConfirm")}
        </Button>
        <Button size="xs" variant="subtle" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </Group>
      {discardEvent.isError && (
        <Text c="red" size="xs">
          {discardEvent.error instanceof ApiError
            ? discardEvent.error.message
            : t("maintenance.card.events.saveError")}
        </Text>
      )}
    </Stack>
  );
}

function CompleteEventForm({
  customerId,
  systemId,
  eventId,
  onDone,
}: {
  customerId: string;
  systemId: string;
  eventId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const completeEvent = useCompleteMaintenanceEvent(customerId, systemId);
  const [executedOn, setExecutedOn] = useState<Date | null>(new Date());
  /**
   * Stände, die der Server bewusst nicht fortgeschrieben hat, weil sie neuer
   * erfasst sind als das Durchführungsdatum. Solange etwas ausgelassen wurde,
   * bleibt das Formular offen — ein Nachtrag, der nichts geschrieben hat,
   * sähe sonst genauso aus wie einer, der alles geschrieben hat.
   */
  const [skipped, setSkipped] = useState<SkippedSoftwareState[]>([]);

  if (skipped.length > 0) {
    return (
      <Alert color="yellow" variant="light" title={t("maintenance.card.events.skippedTitle")}>
        <Stack gap={4}>
          <Text size="xs">{t("maintenance.card.events.skippedHint")}</Text>
          {skipped.map((entry) => (
            <Text key={entry.productId} size="xs">
              {t("maintenance.card.events.skippedEntry", {
                product: entry.productName,
                version: entry.versionLabel,
                asOf: formatIsoDate(entry.existingAsOf),
              })}
            </Text>
          ))}
          <Group>
            <Button size="xs" variant="subtle" onClick={onDone}>
              {t("common.close")}
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }

  return (
    <Group gap="xs" align="flex-end" wrap="wrap">
      {/* Befund U5: tippbares UND anklickbares Feld, wie überall sonst.
          Befund U6: Der Kalender klappte hier nach OBEN auf und verdeckte
          genau die Softwarestandstabelle, gegen die man das Datum abgleicht —
          das Feld sitzt am unteren Rand des aufgeklappten Systems, und
          Floating UIs `flip`-Middleware dreht die Auswahl dann nach oben.
          `flip: false` haelt sie unter dem Feld, `shift` schiebt sie nur
          seitlich in den sichtbaren Bereich; nach unten waechst notfalls die
          Seite mit. Das Tippfeld aus U5 allein loest das nicht — DateInput
          oeffnet die Auswahl auch beim Hineinklicken. */}
      <DateInput
        size="xs"
        label={t("maintenance.card.events.executedOn")}
        placeholder={t("common.datePlaceholder")}
        value={executedOn}
        onChange={setExecutedOn}
        maxDate={new Date()}
        popoverProps={DATE_POPOVER_DOWNWARDS}
        style={{ maxWidth: 200 }}
      />
      <Button
        size="xs"
        disabled={executedOn === null}
        loading={completeEvent.isPending}
        onClick={() => {
          if (!executedOn) return;
          completeEvent.mutate(
            { eventId, executedOn: toLocalDateString(executedOn) },
            {
              onSuccess: (result) => {
                if (result.skippedStates > 0) {
                  setSkipped(result.skipped);
                  return;
                }
                onDone();
              },
            },
          );
        }}
      >
        {t("common.confirm")}
      </Button>
      <Button size="xs" variant="subtle" onClick={onDone}>
        {t("common.cancel")}
      </Button>
      {completeEvent.isError && (
        <Text c="red" size="xs">
          {completeEvent.error instanceof ApiError
            ? completeEvent.error.message
            : t("maintenance.card.events.saveError")}
        </Text>
      )}
    </Group>
  );
}

/** Zeigt Produkt- und Versionsname zu einem bereits gewählten Ziel — die IDs allein wären für Anwender nichtssagend. */
function TargetLabel({ productId, versionId }: { productId: string; versionId: string }) {
  const products = useProducts();
  const versions = useVersions(productId);
  const productName = products.data?.items.find((p) => p.id === productId)?.name ?? productId;
  const versionLabel = versions.data?.find((v) => v.id === versionId)?.label ?? versionId;
  return (
    <Text size="xs">
      {productName} {versionLabel}
    </Text>
  );
}

interface PickedTarget {
  productId: string;
  versionId: string;
}

function NewEventForm({ systemId, onDone }: { systemId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const createEvent = useCreateMaintenanceEvent(systemId);
  const [title, setTitle] = useState("");
  const [plannedFor, setPlannedFor] = useState<Date | null>(null);
  const [targets, setTargets] = useState<PickedTarget[]>([]);

  const canSubmit = title.trim().length > 0 && targets.length > 0;

  // Ein Ziel je Produkt (spiegelt `met_event_product_uq`) — ein zweiter Pick
  // desselben Produkts ersetzt statt zu duplizieren.
  function addTarget(picked: PickedTarget) {
    setTargets((prev) => [...prev.filter((p) => p.productId !== picked.productId), picked]);
  }

  return (
    <Box p="sm" mb="xs" style={{ border: "1px dashed var(--msc-linie-stark)", borderRadius: 8 }}>
      <Stack gap="xs">
        <TextInput
          size="xs"
          label={t("maintenance.card.events.eventTitle")}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <DateInput
          size="xs"
          label={t("maintenance.card.events.plannedFor")}
          placeholder={t("common.datePlaceholder")}
          value={plannedFor}
          onChange={setPlannedFor}
          clearable
          popoverProps={DATE_POPOVER_DOWNWARDS}
          style={{ maxWidth: 200 }}
        />

        <Text size="xs" fw={500}>
          {t("maintenance.card.events.targets")}
        </Text>
        {targets.length === 0 ? (
          <Text c="dimmed" size="xs">
            {t("maintenance.card.events.noTargets")}
          </Text>
        ) : (
          <Stack gap={4}>
            {targets.map((tgt) => (
              <Group key={tgt.productId} gap="xs">
                <TargetLabel productId={tgt.productId} versionId={tgt.versionId} />
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    setTargets((prev) => prev.filter((p) => p.productId !== tgt.productId))
                  }
                >
                  {t("common.remove")}
                </Button>
              </Group>
            ))}
          </Stack>
        )}
        {/* Remount nach jedem Pick (Key = Anzahl der Ziele) — SoftwarePicker
            hält seine Eingabefelder sonst gefüllt und ein zweites Ziel ließe
            sich nicht ohne manuelles Leeren hinzufügen. */}
        <SoftwarePicker
          key={targets.length}
          onPick={addTarget}
          pickLabel={t("maintenance.picker.addTarget")}
        />

        <Group gap="xs">
          <Button
            size="xs"
            disabled={!canSubmit}
            loading={createEvent.isPending}
            onClick={() =>
              createEvent.mutate(
                {
                  title: title.trim(),
                  plannedFor: plannedFor ? toLocalDateString(plannedFor) : undefined,
                  targets,
                },
                { onSuccess: onDone },
              )
            }
          >
            {t("common.save")}
          </Button>
          <Button size="xs" variant="subtle" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        </Group>
        {createEvent.isError && (
          <Text c="red" size="xs">
            {createEvent.error instanceof ApiError
              ? createEvent.error.message
              : t("maintenance.card.events.saveError")}
          </Text>
        )}
      </Stack>
    </Box>
  );
}
